import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useEffect } from "react";
import SafeNumberInput from "./SafeNumberInput";
import { api } from "./utils/api";

export interface ModelParams {
  u: number;
  vol: number;
  s: number;
  r: number;
  t: number;
  type: "call" | "put";
  price: number;
  solveFor: SolveFor;
  timeUnits: "d" | "h";
}

export enum SolveFor {
  u = "u",
  vol = "vol",
  s = "s",
  r = "r",
  t = "t",
  price = "price",
}

const OptionSolver = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) => {
  const [modelParams, setModelParams] = React.useState<ModelParams>({
    u: 0,
    vol: 0,
    s: 0,
    r: 0,
    t: 0,
    type: "call",
    price: 0,
    solveFor: SolveFor.price,
    timeUnits: "d",
  });
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [contract, setContract] = React.useState<string>("");

  // load model parameters from local storage
  useEffect(() => {
    const storedParams = localStorage.getItem("modelParams");
    if (storedParams) {
      setModelParams(JSON.parse(storedParams));
    }
  }, []);

  const solve = () => {
    console.log(
      "Solving with params:",
      modelParams,
      "Solve for:",
      modelParams.solveFor
    );

    // save the model parameters to local storage
    localStorage.setItem("modelParams", JSON.stringify(modelParams));
    setErrorMessage("");

    api
      .post("/option-solver", modelParams)
      .then((response) => {
      const data = response.data;
      if (data.detail) {
        throw new Error(data.detail || "Failed to solve option parameters");
      }
      console.log("Response from server:", data);
      setModelParams((prevParams) => ({
        ...prevParams,
        ...data,
      }));
      })
      .catch((error) => {
      console.error("Error:", error);
      setErrorMessage(
        error.response?.data?.detail || error.message || "Unknown error"
      );
      });
  };

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          
          if (!contract) return;
            api
            .get(`/underlying-expiration?ticker=${encodeURIComponent(contract)}`)
            .then((res) => {
              const data = res.data;
              setModelParams((prev) => ({
              ...prev,
              s: data.strike_price,
              t: data.days_remaining,
              timeUnits: "d",
              type: data.type === "C" ? "call" : "put",
              }));
            })
            .catch(() => setErrorMessage("Failed to fetch contract info"));
        }}
        className="m-4"
      >
        <div className="w-[300px]">
          <label htmlFor="contract">Set expiration using contract:</label>
          <input
            type="text"
            name="contract"
            id="contract"
            value={contract}
            onChange={(e) => setContract(e.target.value)}
          />
        </div>
        <button type="submit" className="hidden">
          Submit
        </button>
      </form>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          solve();
        }}
      >
        <div className="h-full m-4 flex flex-col gap-4 overflow-scroll pb-20 w-[600px]">
          {/* UNDERLYING */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="u">Underlying:</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.u}
                id="u"
                value={modelParams.u}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    u: v,
                  });
                }}
                min={0}
                max={10 ** 7}
              />
            </div>

            <input
              type="radio"
              name="solve_for"
              id="radio_u"
              value={SolveFor.u}
              checked={modelParams.solveFor === SolveFor.u}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.u })
              }
            />
            <label htmlFor="radio_u">Solve for U</label>
          </div>

          {/* STRIKE */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="s">Strike:</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.s}
                id="s"
                value={modelParams.s}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    s: v,
                  });
                }}
                min={0}
                max={10 ** 7}
              />
            </div>

            <input
              type="radio"
              name="solve_for"
              id="radio_s"
              value={SolveFor.s}
              checked={modelParams.solveFor === SolveFor.s}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.s })
              }
            />
            <label htmlFor="radio_s">Solve for S</label>
          </div>

          {/* VOLATILITY */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="vol">Volatility:</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.vol}
                id="vol"
                value={modelParams.vol}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    vol: v,
                  });
                }}
                min={0}
                max={10}
              />
            </div>

            <input
              type="radio"
              name="solve_for"
              id="radio_vol"
              value={SolveFor.vol}
              checked={modelParams.solveFor === SolveFor.vol}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.vol })
              }
            />
            <label htmlFor="radio_vol">Solve for Vol</label>
          </div>

          {/* RATE */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="r">Rate:</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.r}
                id="r"
                value={modelParams.r}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    r: v,
                  });
                }}
                min={0}
                max={1}
              />
            </div>

            <input
              type="radio"
              name="solve_for"
              id="radio_r"
              value={SolveFor.r}
              checked={modelParams.solveFor === SolveFor.r}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.r })
              }
            />
            <label htmlFor="radio_r">Solve for R</label>
          </div>

          {/* TIME */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="t">Time (in units):</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.t}
                id="t"
                value={modelParams.t}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    t: v,
                  });
                }}
                min={0}
                max={365}
              />
            </div>
            <input
              type="radio"
              name="solve_for"
              id="radio_t"
              value={SolveFor.t}
              checked={modelParams.solveFor === SolveFor.t}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.t })
              }
            />
            <label htmlFor="radio_t">Solve for T</label>
          </div>
          {/* PRICE */}
          <div className="flex flex-row gap-4 items-center">
            <div className="w-[300px]">
              <label htmlFor="price">Price:</label>
              <SafeNumberInput
                disabled={modelParams.solveFor === SolveFor.price}
                id="price"
                step={0.01}
                value={modelParams.price}
                onChange={(v) => {
                  setModelParams({
                    ...modelParams,
                    price: v,
                  });
                }}
                min={0}
                max={10 ** 7}
              />
            </div>
            <input
              type="radio"
              name="solve_for"
              id="radio_price"
              value={SolveFor.price}
              checked={modelParams.solveFor === SolveFor.price}
              onChange={() =>
                setModelParams({ ...modelParams, solveFor: SolveFor.price })
              }
            />
            <label htmlFor="radio_price">Solve for Price</label>
          </div>

          {/* TYPE */}
          <div>
            <label htmlFor="type">Type:</label>
            <select
              id="type"
              value={modelParams.type}
              onChange={(e) => {
                console.log("Type changed to:", e.target.value);
                setModelParams({
                  ...modelParams,
                  type: e.target.value as "call" | "put",
                });
              }}
              className="border border-gray-300 rounded p-1"
            >
              <option value="call">Call</option>
              <option value="put">Put</option>
            </select>
          </div>

          {/* TIME UNITS */}
          <div>
            <label htmlFor="timeUnits">Time Units:</label>
            <select
              id="timeUnits"
              value={modelParams.timeUnits}
              onChange={(e) => {
                setModelParams({
                  ...modelParams,
                  timeUnits: e.target.value as "d" | "h",
                });
              }}
              className="border border-gray-300 rounded p-1"
            >
              <option value="d">Days</option>
              <option value="h">Hours</option>
            </select>
          </div>

          <button type="submit" className="btn btn-default w-3/6">
            Solve
          </button>

          {errorMessage && (
            <div className="text-red-500">
              <strong>Error:</strong> {errorMessage}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default OptionSolver;
